import React from "react";
import { motion } from "framer-motion";
import { INTERIOR_IMAGES, WORK_GALLERY } from "../data/content";

export const InteriorGallery = () => {
  return (
    <section
      id="glimpses"
      data-testid="interior-gallery"
      className="relative border-t border-[color:var(--border)] bg-[color:var(--surface)] px-6 py-32 md:px-12 md:py-48"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-14 flex flex-col gap-4 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="chip mb-6">
              <span className="dot" />
              04 · Interior
            </div>
            <h2 className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl">
              Glimpses of
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                99 Salon.
              </span>
            </h2>
          </div>
          <p className="max-w-xs text-sm text-[color:var(--muted)]">
            Four frames from the studio floor and the front gate — this is
            actually where we work, every day.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:gap-4">
          <TallCard src={INTERIOR_IMAGES[0]} label="The Address · Patiala" span="md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-[4/5]" />
          <TallCard src={INTERIOR_IMAGES[1]} label="Styling Floor" span="md:col-span-5 aspect-[5/4]" />
          <TallCard src={INTERIOR_IMAGES[2]} label="Mirrors & Chairs" span="md:col-span-3 aspect-square" />
          <TallCard src={INTERIOR_IMAGES[3]} label="Vanity Stations" span="md:col-span-2 aspect-[3/4]" />
        </div>
      </div>
    </section>
  );
};

const TallCard = ({ src, label, span }) => (
  <motion.figure
    initial={{ opacity: 0, y: 40, clipPath: "inset(20% 20% 20% 20%)" }}
    whileInView={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
    className={`group relative overflow-hidden ${span}`}
  >
    <img
      src={src}
      alt={label}
      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
    <figcaption className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
      {label}
    </figcaption>
  </motion.figure>
);

export const WorkGallery = () => {
  return (
    <section
      id="work"
      data-testid="work-gallery"
      className="relative border-t border-[color:var(--border)] bg-[color:var(--bg)] px-6 py-32 md:px-12 md:py-48"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-14 flex flex-col gap-4 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="chip mb-6">
              <span className="dot" />
              05 · Look at our work
            </div>
            <h2 className="font-serif text-5xl leading-[0.9] tracking-tight md:text-7xl lg:text-8xl">
              Some of what
              <br />
              <span className="italic-serif text-[color:var(--primary)]">
                walked out.
              </span>
            </h2>
          </div>
          <p className="max-w-xs text-sm text-[color:var(--muted)]">
            Bridal, hair, nails, skin, makeup — a sampling. Placeholder frames
            for now; real client edits ship next.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
          {WORK_GALLERY.map((w, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 60, clipPath: "inset(15% 0 15% 0)" }}
              whileInView={{ opacity: 1, y: 0, clipPath: "inset(0 0 0 0)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 1,
                delay: i * 0.06,
                ease: [0.7, 0, 0.2, 1],
              }}
              className={`group relative overflow-hidden ${
                i === 0 ? "col-span-2 aspect-[16/10] md:col-span-2 md:aspect-[3/2]" : "aspect-[3/4]"
              }`}
              data-testid={`work-item-${i}`}
            >
              <img
                src={w.src}
                alt={w.caption}
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-90 transition-opacity group-hover:opacity-100" />
              <figcaption className="absolute inset-x-4 bottom-4 flex items-end justify-between">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/70">
                    0{i + 1} · {w.title}
                  </div>
                  <div className="mt-1 font-serif text-xl italic text-white md:text-2xl">
                    {w.caption}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
